/* One exercise, as `Builder planu - tydzien - programator` draws it.
 *
 * The numbers are that file's: a 14px-radius card on `--bg-card` with a hairline and
 * `--shadow-card`, a head at 6px/10px padding and 9px gaps, and a foot of two columns
 * (`minmax(0,1fr) minmax(0,1.3fr)`) at 3px/9px.
 *
 * ── `--text-3` carries no words here ────────────────────────────────────────────────────────
 * The feature's standing deviation: KANON's tertiary ink measures 2.80:1 on a card and 4.02:1 in
 * the dark theme. The canon reaches for it on the muscle line, the summary and the fold; those read
 * `--text-2` here and the hierarchy is carried by size and weight instead.
 *
 * ── Grouped cards are STITCHED, not nested ──────────────────────────────────────────────────
 * A superset is one ring around several cards (`DayForm.module.css` `.bracket`). The members inside
 * it drop their own border and shadow and overlap by 2px, so the run reads as one block rather than
 * as three cards inside a box — the canon's own `marginBottom: -2px`.
 */

.block {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* Focus lands here by SCRIPT — the card an add out of the library drawer just made — so the ring
 * has to be drawn, or a keyboard trainer is put on a card with nothing to say they are on it. The
 * same argument, and the same declaration, as the week card's `.card:focus-visible`. Inset, because
 * the list clips at its own edges. */
.block:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--outline-inset);
}

/* Inside a superset ring the card is a segment of one block. The ring is the edge; these are not. */
.block[data-grouped] {
  border-color: transparent;
  box-shadow: none;
}

.block[data-grouped]:not(:last-child) {
  margin-bottom: var(--space-minus-2px);
}

/* A selection is running and this card cannot join it — a warm-up, or a member of another block.
 * The reason is in the checkbox's accessible name; this is the second channel, not the only one. */
.block[data-ineligible] {
  opacity: 0.45;
}

/* Picked. A neutral inset rail, in the same place the canon marks a selected exercise, because the
 * accent on this surface belongs to the JOIN in the bar above and nowhere else. */
.block[data-picked] {
  box-shadow: var(--ring-lifted);
}

/* ── The head row ────────────────────────────────────────────────────────────────────────── */

.head {
  position: relative;
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  min-width: 0;
  padding: var(--space-6px) var(--space-10px);
}

/* The mock's `headSt` and `grpBar` both carry `cursor: grab`, and so do these — but only where the
 * strip IS the drag source. A viewer and a running superset selection bind no gesture, and a hand
 * offering to pick up a row that cannot move is a lie the mock never tells, because in the mock it
 * always can. `DragStrip` sets the attribute exactly when it bound one. */
.head[data-drag],
.groupBar[data-drag] {
  cursor: grab;
}

/* The rule under the head belongs to the OPEN card: a folded one is a single row and a hairline
 * across its bottom would draw a line under nothing. */
.block:not([data-folded]) .head {
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The superset tick. A real checkbox, sized and drawn as the canon's 20px box: `appearance: none`
 * so the browser's own control does not fight the geometry, and the check painted as a fill rather
 * than as a glyph — ink on ink, never the accent. */
.pickBox {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
}

.pickMark {
  position: absolute;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--bg-card);
  pointer-events: none;
}

.pick {
  width: 20px;
  height: 20px;
  margin: var(--space-0);
  appearance: none;
  cursor: pointer;
  background: transparent;
  border: var(--border-pip) solid var(--border-2);
  border-radius: var(--radius-xs);
}

.pick:checked {
  background: var(--text);
  border-color: var(--text);
}

.pick:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.pick[aria-disabled='true'] {
  cursor: default;
}

/* The position marker — `1`, `2a`, `R3`. `min-width` rather than `width`: `2a` and `R3` are two
 * glyphs where `1` is one, and the canon lets the badge grow rather than clipping. */
.marker {
  display: grid;
  flex: none;
  place-items: center;
  min-width: 24px;
  height: 22px;
  padding: var(--space-0) var(--space-6px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
  font-variant-numeric: tabular-nums;
}

/* Inside a superset the marker takes the frame's ink, so `2a` / `2b` read as one decision. The
 * mock's own `e.badge` uses `var(--accent)` here, which is the same hue the ring and the pill wear. */
.bracketMarker {
  color: var(--accent);
  border-color: var(--accent);
}

.thumbnail {
  flex: none;
  width: 40px;
  height: 28px;
  object-fit: cover;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

.identity {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.name {
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-plan);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muscleLine {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  overflow: hidden;
  font-size: var(--fs-pl-sub);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-mg-line);
  letter-spacing: var(--ls-block);
  text-transform: uppercase;
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.musclePip {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: var(--radius-hair);
}

.headSpacer {
  flex: 1;
  min-width: var(--space-1);
}

/* "3 × 10" while the card is folded — the whole prescription, in the space the table left. */
.summary {
  flex: none;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The head's `Rozgrzewka` chip — the mock's `prepBtn`, value for value: 22px tall at `0 8px`, radius
 * 7, a hairline in `--border`, 10px/600 on the tertiary ink, with the pulse trace 5px before the
 * word. Boxless otherwise: no fill, so it reads as an offer on the row rather than as a second
 * button competing with the ⋯ beside it. 10px is the 12 August 2026 audit's floor exactly, so the
 * word needs no exception.
 *
 * It spent one day (15 September 2026) as a bare glyph in the fold's box below, and the owner put
 * the word back the next: the row's other small controls are ways of LOOKING at the card, and the
 * only one that changes the day cannot be a fourth square among them. */
.warmupChip {
  display: inline-flex;
  flex: none;
  gap: var(--space-5px);
  align-items: center;
  height: 22px;
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);

  /* Stylelint accepts no `currentcolor` on a `stroke` — the same hand-off `.remove` and
   * `.iconButton` already make. */
  --warmup-chip-ink: var(--text-3);
}

.warmupChip:hover {
  --warmup-chip-ink: var(--text);

  color: var(--text);
  border-color: var(--border-2);
}

.warmupChip:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.warmupChipIcon {
  display: block;
  width: 12px;
  height: 12px;
  stroke: var(--warmup-chip-ink);
  fill: none;
}

/* The fold chevron: the mock's geometry, a 20px boxless square on the tertiary ink. The class keeps
 * the generic name it took on 15 September 2026, when the `Rozgrzewka` offer briefly shared this
 * box; `ExerciseMenu`'s trash points at `--icon-ink` from one file away. */
.iconButton {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: var(--space-0);
  color: var(--text-3);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-5px);

  /* The glyph's ink, carried as a custom property rather than `currentcolor`: stylelint's value
   * allowlist takes `currentColor` and `value-keyword-case` demands `currentcolor`, so the two
   * rules cannot both be satisfied on a `stroke`. Same shape as `DayChrome`'s `--mode-ink`. */
  --icon-ink: var(--text-3);
}

/* The pointer target the 20px square does not carry on its own — WCAG 2.2 SC 2.5.8's floor, bought
 * with a pseudo-element so the canon's geometry ships unchanged. */
.iconButton::after {
  position: absolute;
  inset: var(--hit-extend);
  content: "";
}

.iconButton:hover {
  --icon-ink: var(--text);

  color: var(--text);
}

.iconButton:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.iconGlyph {
  display: block;
  width: 13px;
  height: 13px;
  stroke: var(--icon-ink);
  fill: none;
  transition: transform var(--dur-fold) var(--ease);
}

.foldIconUp {
  transform: rotate(180deg);
}

/* ── The day's warm-up RUN ────────────────────────────────────────────────────────────────
 *
 * The mock draws a warm-up as a compact row that expands, not as a card: transparent until it is
 * opened, `--bg-card` inside a hairline once it is, over the mock's own .16s. The thumbnail drops
 * to 30 x 22, the row's padding to `5px 8px 5px 6px`, and the marker takes the warm-up blue. */
.block[data-warmup] {
  background: transparent;
  border-color: transparent;
  border-radius: var(--radius-8px);
  box-shadow: none;
  transition:
    background var(--dur-fold) var(--ease-default),
    border-color var(--dur-fold) var(--ease-default);
}

.block[data-warmup]:not([data-folded]) {
  background: var(--bg-card);
  border-color: var(--border);
}

.block[data-warmup] .head {
  gap: var(--space-9px);
  padding: var(--space-5px) var(--space-2) var(--space-5px) var(--space-6px);
}

/* The run's rows carry no rule under the head: the table below opens on the inset ground with its
 * own top edge, which is the only line the mock draws there. */
.block[data-warmup]:not([data-folded]) .head {
  border-bottom: 0;
}

.block[data-warmup] .thumbnail {
  width: 30px;
  height: 22px;
  border-radius: var(--radius-xs);
}

.block[data-warmup] .marker {
  color: var(--warm-blue);
  border-color: var(--warm-blue);
}


/* ── A lift's own warm-ups, standing INSIDE its card ─────────────────────────────────────────
 *
 * The mock's `prepBox`, `prepHead` and `grpBar`, value for value. A mini-group is a warm-up bound to
 * the one exercise it prepares, and the mock draws it as one card: the ramps in a railed box above
 * the lift's head, not as rows beside it. The bracket that used to wrap the two is gone and
 * `.bracket` in `DayForm.module.css` belongs to the superset alone.
 *
 * `--warm-blue` and `--warm-bg` are byte-identical to the mock's `--blue` / `--blue-soft` in both
 * themes, and they are the right tokens rather than a coincidence: this is the STRUCTURAL warm-up
 * blue, the same one the day's own run wears on its rail. */

/* `margin: 4px 9px 0; padding: 8px; radius 10; borderLeft: 2px solid var(--blue)`. The rail is a
 * `border-left` and not a pseudo-element for the same reason the day's run uses one: it is an edge
 * of the box, and the box has to keep its corners on the other three sides. */
.prepBox {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-6px);
  margin: var(--space-1) var(--space-9px) var(--space-0);
  padding: var(--space-2);
  background: var(--warm-bg);
  border-left: var(--border-rail) solid var(--warm-blue);
  border-radius: var(--radius-sm);
}

/* The box's own heading, in the warm-up blue — the same two sizes the day's RUN prints its title and
 * its count at, so the two warm-up headings in one panel are one treatment rather than two. */
.prepHead {
  display: flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: var(--space-4);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-column);
  text-transform: uppercase;
  color: var(--warm-blue);
  white-space: nowrap;

  /* Stylelint's value allowlist takes no `currentcolor`, so the heading's ink is published as a
   * property and the glyph reads it — the same shape `.iconButton` already uses. */
  --prep-head-ink: var(--warm-blue);
}

.prepFlame {
  display: block;
  flex: none;
  width: 11px;
  height: 11px;
  fill: none;
  stroke: var(--prep-head-ink);
}

.prepTitle {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The mock's own: the count drops the tracking, the uppercase and the blue, so the heading reads as
 * one label with a number after it rather than as two labels. */
.prepCount {
  flex: none;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
  text-transform: none;
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The mock's `grpBar`: `margin: 0 10px; padding: 7px 0 2px; gap 8`, the word, and a hairline running
 * to the card's edge (the mock's grip went on 15 September 2026). It exists only while the card holds
 * preps — the thing being dragged is the lift AND its ramps. */
.groupBar {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-0) var(--space-10px);
  padding: var(--space-7px) var(--space-0) var(--space-2px);
}

.groupTag {
  flex: none;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-column);
  text-transform: uppercase;
  color: var(--text-3);
  white-space: nowrap;
}

.groupRule {
  flex: 1;
  height: var(--space-1px);
  background: var(--border);
}

/* ── One ramp, as the mock's mini card ───────────────────────────────────────────────────────
 *
 * `radius 9, overflow hidden, background var(--bg-card), border 1px solid var(--border)`. It is the
 * same component as every other exercise on the surface, so the only thing this says is where it is
 * standing: a warm-up in the day's RUN is a bare row that gains a ground when it opens, and the same
 * warm-up inside a lift's box is a card that always has one. `[data-prep]` therefore has to beat
 * `[data-warmup]`, and it is written after it for exactly that. */
.block[data-prep],
.block[data-prep]:not([data-folded]) {
  background: var(--bg-card);
  border-color: var(--border);
  border-radius: var(--radius-mark);
  box-shadow: none;
}

/* The mock's `cardIn`, on the ramp a targeted library pick has just made and on nothing else. The
 * card mounts with the attribute, so the animation runs once and the resting state is the one a
 * still viewer sees — `tokens/base.css` collapses the duration under `prefers-reduced-motion`. */
@keyframes prep-card-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.block[data-prep][data-fresh] {
  animation: prep-card-in var(--dur-card-in) var(--ease-fp) both;
}

/* ── The table, and the foot under it ────────────────────────────────────────────────────── */

.main {
  min-width: 0;
}

/* The canon's foot: the rule statement and the note, side by side.
 *
 * ── The rule's track is `auto`, not `1fr` (8 September 2026) ─────────────────────────────────
 * It was `1fr / 1.3fr`, which is the mock's own split — and the mock fills its left track with a
 * spring and a pencil into the rule editor. This build draws neither, because there is no rule
 * editor to open (`features/plan-progression`'s own header), so a `1fr` track put two words at the
 * left end of half the foot and left the rest empty. The owner read exactly that on the running app:
 * *"sztucznie wielki i suchy"*.
 *
 * `auto` gives the statement its own width and every pixel it does not need to the note, which is a
 * field a trainer types two sentences into. Measured in the 574px panel: the foot's 525px of content
 * was split 226 / 293 by the fractions, and is 152 / 367 now, so the note gained 74px.
 * The track goes back to `1fr` with the pencil, because then there is something for the room to be
 * for. `minmax(0, auto)` rather than bare `auto` so a long axis list ellipsises inside the chip
 * instead of pushing the note off the card. */
.foot {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: var(--space-6px);
  align-items: start;
  padding: var(--space-3px) var(--space-9px) var(--space-1);
}

.foot[data-columns='one'] {
  grid-template-columns: minmax(0, 1fr);
}

/* The mock's `noteText`: `4px 9px`, radius 6, dashed `--border-2` over nothing, 10.5px/500 at 1.35.
 * Transparent, so it reads as an invitation rather than as a filled field competing with the table
 * above it.
 *
 * ── The HEIGHT is the owner's, not the mock's (8 September 2026) ─────────────────────────────
 * The mock draws a 22px box, which holds a phrase and starts scrolling on the second. The owner
 * asked for room for **two sentences by default**, so the row is `--h-card-foot` — twice the mock's
 * — and the token carries the reasoning. The dashed edge, the two-column grid and everything else
 * about the foot are the mock's, unchanged; only the row is taller. It was 42px until 7 September
 * 2026, then 22, and this is neither: 44 is the doubling, stated once and read by both columns. */
.noteField {
  display: block;
  width: 100%;
  min-height: var(--h-card-foot);
  padding: var(--space-1) var(--space-9px);
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint-dense);
  color: var(--text);
  resize: vertical;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-xs);
}

/* The day being READ. The note is the coaching tip the athlete will meet in the logger, so it stays
 * legible and selectable; only the invitation to write goes. */
.noteField[readonly] {
  color: var(--text-2);
  resize: none;
  background: var(--bg-inset);
  border-style: solid;
}

.noteField::placeholder {
  color: var(--text-2);
}

.noteField:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* ── A sentence the canon does not draw, in the vocabulary it does ───────────────────────── */

/* A ladder with descents and no top — a shape only the wire can produce. Named, never repaired. */
.warning {
  display: block;
  margin: var(--space-0) var(--space-10px) var(--space-2);
  padding: var(--space-6px) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--danger-text-strong);
  background: var(--danger-soft);
  border-radius: var(--radius-xs);
}

/* The muscle line's height, held while the catalogue that names the muscle has not answered — a
 * change under review reserves it (`ExerciseIdentity`), the day form does not. */
.muscleReserve {
  min-height: 1lh;
}
